import { requireService, wrap } from '@repo/di-ez';
import { AddTableLight, DotsEditCustom } from '@repo/go-icons';
import type { GridApi } from '@repo/go-table';
import { Button, Collapse, Drawer, Loader, Scrollbar, Tooltip } from '@repo/go-ui';
import type { MutableRefObject } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useStore } from 'src/store';
import { GOPLAN_TABLE_SYSTEM_TEMPLATES_EDITING } from 'src/store/roles-store/roles';

import type { DisplayManager } from '../table-settings/display-manager';
import { applySettingsToTable } from '../utils';

import { EditTemplateModal } from './edit-template-modal';
import { ExportTemplatesModal } from './export-templates-modal';
import type { TWellTableTemplate, TWellTableTemplateData } from './wells-table-templates.api';
import styles from './wells-table-templates.module.scss';

const TableTemplateItem = wrap<{
  template: TWellTableTemplate;
  isCanEditPublicTemplates?: boolean;
  isSystemCreationAvailable?: boolean;
  isPrivateCreationAvailable?: boolean;
  onEditTemplate: () => void;
  onDeleteTemplate: () => void;
  onCopyTemplate: () => void;
  onApplyToTable: () => void;
}>(function TableTemplateItem({
  template,
  isCanEditPublicTemplates = false,
  isSystemCreationAvailable = true,
  isPrivateCreationAvailable = true,
  onEditTemplate,
  onDeleteTemplate,
  onCopyTemplate,
  onApplyToTable,
}) {
  const { t } = useTranslation();
  const [isTooltipOpen, setIsTooltipOpen] = useState(false);
  const copyTitle =
    template.type === 'private' ? t('wells:Templates.copyToPublic') : t('wells:Templates.copyToPrivate');

  return (
    <div className={styles.tableTemplateItem}>
      <div className={styles.tableTemplateItemContent}>
        <p className={styles.tableTemplateItemName}>{template.name}</p>
        <p className={styles.tableTemplateItemDescription}>{template.description}</p>
      </div>
      <Tooltip
        open={isTooltipOpen}
        containerClassName={styles.tableTemplateItemTooltipContent}
        onOpenChange={(val) => setIsTooltipOpen(val)}
        title={
          <ul style={{ padding: 0, margin: 0, listStyle: 'none' }}>
            <li>
              <Button variant="flat" className={styles.tableTemplateItemTooltipButton} onClick={onApplyToTable}>
                {t('wells:Templates.applyTemplate')}
              </Button>
            </li>
            {(template.type === 'private' || isCanEditPublicTemplates) && (
              <li>
                <Button
                  variant="flat"
                  className={styles.tableTemplateItemTooltipButton}
                  onClick={() => {
                    onEditTemplate();
                    setIsTooltipOpen(false);
                  }}
                >
                  {t('common:edit')}
                </Button>
              </li>
            )}
            {(isCanEditPublicTemplates || template.type === 'system') &&
              ((template.type === 'system' && isPrivateCreationAvailable) ||
                (template.type === 'private' && isSystemCreationAvailable)) && (
                <li>
                  <Button
                    variant="flat"
                    className={styles.tableTemplateItemTooltipButton}
                    onClick={() => {
                      onCopyTemplate();
                      setIsTooltipOpen(false);
                    }}
                  >
                    {copyTitle}
                  </Button>
                </li>
              )}
            {(template.type === 'private' || isCanEditPublicTemplates) && (
              <li>
                <Button
                  variant="flat"
                  style={{ color: 'var(--go-red-light)' }}
                  className={styles.tableTemplateItemTooltipButton}
                  onClick={() => {
                    onDeleteTemplate();
                    setIsTooltipOpen(false);
                  }}
                >
                  {t('common:Buttons.delete')}
                </Button>
              </li>
            )}
          </ul>
        }
      >
        <Button
          variant="flat"
          icon={<DotsEditCustom style={{ color: 'var(--go-text)', fontSize: 20 }} />}
          className={styles.tableTemplateItemButton}
        />
      </Tooltip>
    </div>
  );
});

type Props = {
  displayManager: DisplayManager;
  tableApi: MutableRefObject<GridApi>;
  onExportTemplates: (data?: TWellTableTemplateData | 'currentTableSettings') => void;
};

export const WellsTableTemplates = wrap<Props>(function WellsTableTemplates({
  displayManager,
  tableApi,
  onExportTemplates,
}) {
  const { t } = useTranslation();
  const pageStore = requireService('wellsPageStore');
  const { tableTemplatesStore } = pageStore;
  const { rolesStore } = useStore();

  const { templates, isTemplatesInitiated } = tableTemplatesStore;

  const applyTemplateToTable = (template: TWellTableTemplate) => {
    applySettingsToTable(tableApi, template.data, displayManager.columnsGrouping, {
      defaultColumnsOrderModel: tableTemplatesStore.defaultColumnsOrderModel,
    });
  };

  if (!isTemplatesInitiated) {
    return <Loader />;
  }

  const isCanEditPublicTemplates = rolesStore.hasAccess([GOPLAN_TABLE_SYSTEM_TEMPLATES_EDITING]);

  return (
    <>
      <Drawer
        title={<h2 className="h2">{t('wells:Templates.title')}</h2>}
        open={pageStore.isTableTemplatesOpen}
        style={{ width: 400 }}
        onClose={() => pageStore.setIsTableTemplatesOpen(false)}
      >
        <div className={styles.wrapper}>
          <Scrollbar>
            <div className={styles.templatesList}>
              <Collapse.Panel
                panelKey="system"
                className={styles.templatesListPanel}
                header={t('wells:Templates.public')}
              >
                <div className={styles.templatesListItems}>
                  {templates.system.map((template) => {
                    const onApplyToTable = () => {
                      applyTemplateToTable(template);
                    };

                    return (
                      <TableTemplateItem
                        key={template.id}
                        template={template}
                        isCanEditPublicTemplates={isCanEditPublicTemplates}
                        isSystemCreationAvailable={tableTemplatesStore.isSystemCreationAvailable}
                        isPrivateCreationAvailable={tableTemplatesStore.isPrivateCreationAvailable}
                        onEditTemplate={() => tableTemplatesStore.setEditTemplateModalState(template)}
                        onDeleteTemplate={() => tableTemplatesStore.deleteTemplate(template.id)}
                        onCopyTemplate={() => tableTemplatesStore.copyTemplate(template, 'private')}
                        onApplyToTable={onApplyToTable}
                      />
                    );
                  })}
                </div>
              </Collapse.Panel>
              <Collapse.Panel
                className={styles.templatesListPanel}
                panelKey="private"
                header={t('wells:Templates.private')}
              >
                <div className={styles.templatesListItems}>
                  {templates.private.map((template) => {
                    const onApplyToTable = () => {
                      applyTemplateToTable(template);
                    };

                    return (
                      <TableTemplateItem
                        key={template.id}
                        template={template}
                        isCanEditPublicTemplates={isCanEditPublicTemplates}
                        isSystemCreationAvailable={tableTemplatesStore.isSystemCreationAvailable}
                        isPrivateCreationAvailable={tableTemplatesStore.isPrivateCreationAvailable}
                        onEditTemplate={() => tableTemplatesStore.setEditTemplateModalState(template)}
                        onDeleteTemplate={() => tableTemplatesStore.deleteTemplate(template.id)}
                        onCopyTemplate={() => tableTemplatesStore.copyTemplate(template, 'system')}
                        onApplyToTable={onApplyToTable}
                      />
                    );
                  })}
                </div>
              </Collapse.Panel>
            </div>
          </Scrollbar>
          <Button
            disabled={!tableTemplatesStore.isSystemCreationAvailable && !tableTemplatesStore.isPrivateCreationAvailable}
            icon={<AddTableLight style={{ color: 'var(--go-text)', fontSize: 20 }} />}
            className={styles.createTemplateButton}
            variant="success"
            title={t('wells:Templates.createNewTemplate')}
            onClick={() => tableTemplatesStore.setEditTemplateModalState('new')}
          >
            {t('wells:Templates.createNewTemplate')}
          </Button>
        </div>
      </Drawer>
      <EditTemplateModal displayManager={displayManager} templatesStore={tableTemplatesStore} />
      <ExportTemplatesModal templatesStore={tableTemplatesStore} onExportTemplates={onExportTemplates} />
    </>
  );
});
